import React, { useContext } from "react";
import { useMutation } from "@apollo/client";
import { Form } from "react-final-form";
import { Box, Button, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { MUIFormSectionWFields } from "@upptic/module-ui";

import { ModalContext } from "contexts";
import { AuthSection } from "components/auth";
import { RESEND_ACTIVATION } from "../../../api/mutations/activationEmail";
import { useRequestErrorHandler } from "v2/hooks/useRequestErrorHandler";
import validators from "utils/validators";
import formatters from "utils/formatters";

export interface FormData {
  emailAddress: string;
}

function ActivateAccountErrorPage() {
  const { t } = useTranslation("auth");
  const { t: tg } = useTranslation("general");
  const [resendEmail] = useMutation(RESEND_ACTIVATION);

  const setModal = useContext(ModalContext);

  const handleError = useRequestErrorHandler();

  const defaultState: FormData = {
    emailAddress: "",
  };

  const resendActivationEmail = async (values: FormData) => {
    const response = await handleError(
      resendEmail({
        variables: {
          email: values.emailAddress,
        },
      }),
    );

    if (response) {
      setModal({
        type: "set",
        props: {
          title: "Done",
          content: `If there was an account associated with the email you provided, the activation email has been sent to ${values.emailAddress}.`,
        },
      });
    }
  };

  const fields = [
    {
      name: "emailAddress",
      label: t<string>("email"),
      placeholder: "Enter Email Address",
      validate: validators.composeValidators(
        false,
        validators.required,
        validators.email,
      ),
      formatter: formatters.trim,
      xs: 12,
    },
  ];

  return (
    <Form onSubmit={resendActivationEmail} initialValues={defaultState}>
      {({ handleSubmit, submitting }) => (
        <form onSubmit={handleSubmit} autoComplete="off">
          <AuthSection title="Activate account">
            <Box
              display="flex"
              flexDirection="column"
              alignItems="stretch"
              width="100%"
            >
              <Typography
                component="p"
                sx={{
                  margin: "20px 0",
                  textAlign: "center",
                }}
              >
                The activate account link is invalid or it has expired. You can
                resend a new activation link by submitting your email below.
              </Typography>
              <MUIFormSectionWFields disablePadding fields={fields} />
              <Stack
                sx={{ width: "100%" }}
                spacing={2}
                pt={2}
                direction="row"
                justifyContent="flex-end"
              >
                <Button
                  variant="contained"
                  sx={{ minWidth: "130px" }}
                  type="submit"
                  disabled={submitting}
                >
                  Send new activation link
                </Button>
              </Stack>
            </Box>
          </AuthSection>
        </form>
      )}
    </Form>
  );
}

export default ActivateAccountErrorPage;
